import store from "@/store";
import DcbService from "@/services/dcbService";
import getUserSessionData from "@/functions/getUserSessionData";
import {ServiceIds} from "../config";
import getServiceId from "@/functions/getServiceId";
import hasGameAccess from "@/functions/hasGameAccess";
import PredictorService from "@/services/predictorService";
import MtnZAService from "@/services/mtnZAService";
import {Brands} from "@/brand/brandConfig";
import WorldPlayService from "@/services/worldPlayService";
import cleanMtnUser from "@/functions/cleanMtnUser";
import HeaderEnrichmentService from "@/services/headerEnrichmentService";
import {handleMyBundleEntry, isMyBundleEntry} from "@/functions/myBundleEntry";
import {isVodacomBrand} from "@/functions/brandHelpers";
import {environmentConfig} from "@/config/enviromentConfig";

const TPF_HOSTED_ROUTES = ['RequestOtp', 'SubmitOtp', 'SubscriptionView', 'ConfirmSubscription'];
const SUBSCRIPTION_ID_PARAMS = ['subscription-id', 'subscriptionId'];

function getSubscriptionIdParam(params) {
    for (const key of SUBSCRIPTION_ID_PARAMS) {
        const value = params.get(key);
        if (value) return value;
    }
    return undefined;
}

export async function userAuthenticate(to, from, next) {
    // Tealium is injected once from App.vue — never from this guard. Injecting here appended
    // another utag.js on every navigation and made each route change wait for it to load.
    const {brand} = store.state.brandConfig;
    const params = new URLSearchParams(window.location.search);

    if (isVodacomBrand(brand)) {
        const subscriptionId = getSubscriptionIdParam(params);

        if (subscriptionId && !store.state.user) {
            return await subscriptionIdAuth(subscriptionId, next, params.get('campaignId') || params.get('cid'), params.get('et'));
        }

        if (TPF_HOSTED_ROUTES.includes(to.name) && !subscriptionId && !params.has('e-msisdn')) {
            window.location.assign(environmentConfig.tpfHost);
            return next(false);
        }
    }

    if (brand === Brands.VodacomEvinaSouthAfricaEnglish) await userAuthenticateEncryptedFlow(to, from, next)
    else if (brand === Brands.MTNWorldPlaySouthAfticaEnglish) await userAuthenticateWorldPlay(to, from, next)
    else if (brand === Brands.TelkomWorldPlaySouthAfticaEnglish) await userAuthenticateWorldPlay(to, from, next)
    else if (brand === Brands.WorldPlaySouthAfticaEnglish) await userAuthenticateWorldPlay(to, from, next)
    else if (brand === Brands.WorldPlayRuggaNationSouthAfricaEnglish) await userAuthenticateWorldPlay(to, from, next)
    else await userAuthenticateNormalFlow(to, from, next);
}

const WP_PLAY_HEADER_ENRICHMENT_HOSTNAME = 'wp-play.rugby-zone.co.za';
const WP_PLAY_HE_ATTEMPTED_STORAGE_KEY = 'wpPlayHeaderEnrichmentAttempted';

const WP_UNSUBSCRIBED_ALLOWED_ROUTES = [
    'HomeView', 'SubscriptionView', 'TermsAndConditions',
    'NewsView', 'NewsHome', 'NewsArticles', 'NewsVideos', 'NewsArticle', 'NewsExclusive', 'NewsVideo',
];

async function userAuthenticateWorldPlay(to, from, next) {
    if (store.state.user) {
        store.commit('setIsLoading', false);
        if (!hasGameAccess(store.state.user) && !WP_UNSUBSCRIBED_ALLOWED_ROUTES.includes(to.name)) {
            return next({ name: 'SubscriptionView' });
        }
        return next();
    }

    if (store.state.brandConfig.brand === Brands.WorldPlayRuggaNationSouthAfricaEnglish && isMyBundleEntry()) {
        return await myBundleEntryFlow(to, next);
    }

    const allowedRoutes = ['TermsAndConditions'];
    if (allowedRoutes.includes(to.name)) return next();

    if (isWpPlayHeaderEnrichmentHost()) {
        return await userAuthenticateWorldPlayHeaderEnrichment(to, next);
    }

    const urlParams = new URLSearchParams(window.location.search);
    const {productId} = store.state.brandConfig;
    const wpMsisdn = urlParams.get('tn');
    const urlMsisdn = urlParams.get('msisdn') ?? localStorage.getItem(`msisdn`);
    const msisdn = wpMsisdn ? wpMsisdn : urlMsisdn

    if (!msisdn) {
        if (WP_UNSUBSCRIBED_ALLOWED_ROUTES.includes(to.name)) return next();
        return next({ name: 'SubscriptionView' });
    }

    localStorage.setItem(`msisdn`, msisdn)
    const getUser = await WorldPlayService.getUser(msisdn, productId);
    const userStatus = `${getUser?.Status || ''}`.toLowerCase();
    const validUser = userStatus === 'pending' || userStatus === 'active';
    if (validUser) {
        const cleanedUser = cleanMtnUser({...getUser, msisdn: getUser.Msisdn});
        await setUserDataMtn(cleanedUser, next, to);
    } else {
        window.open(getWorldPlayDoiUrl(), '_self');
        return next()
    }
}

async function myBundleEntryFlow(to, next) {
    store.commit('setIsLoading', true);
    const entry = await handleMyBundleEntry(store.state.brandConfig.productId);
    if (!entry.entered) {
        store.commit('setIsLoading', false);
        return next({name: 'SubscriptionView'});
    }
    store.commit('setMyBundle', true);
    return await setUserDataMtn(toGameAccessUser(entry.user), next, to);
}

// hasGameAccess reads the cleaned shape (status/freePlayService); the endpoint returns the dep shape.
function toGameAccessUser(user) {
    const {freePlayServiceId} = store.state.brandConfig;
    return {
        ...user,
        status: `${user.statusName || ''}`.toUpperCase() === 'ACTIVE' ? 'Active' : 'Pending',
        freePlayService: user.serviceName === freePlayServiceId,
    };
}

function isWpPlayHeaderEnrichmentHost() {
    return window.location.hostname === WP_PLAY_HEADER_ENRICHMENT_HOSTNAME;
}

function getWorldPlayDoiUrl() {
    const {brand} = store.state.brandConfig;
    return brand === Brands.WorldPlayRuggaNationSouthAfricaEnglish
        ? 'http://playbundles.mtn.co.za/lander/sports'
        : 'http://doi.mtndep.co.za/service/11008';
}

async function userAuthenticateWorldPlayHeaderEnrichment(to, next) {
    const allowedRoutes = ['TermsAndConditions', 'RequestOtp', 'SubmitOtp'];
    if (allowedRoutes.includes(to.name)) return next();

    const urlParams = new URLSearchParams(window.location.search);
    const msisdn = await resolveWorldPlayMsisdn(urlParams);

    if (!msisdn) {
        const hasAttemptedHeaderEnrichment = sessionStorage.getItem(WP_PLAY_HE_ATTEMPTED_STORAGE_KEY) === 'true';
        if (!hasAttemptedHeaderEnrichment) {
            sessionStorage.setItem(WP_PLAY_HE_ATTEMPTED_STORAGE_KEY, 'true');
            HeaderEnrichmentService.redirectToMtnHeaderEnrichment(urlParams);
            return next();
        }

        // Header enrichment did not return an MSISDN; fall back to the existing WP flow.
        window.open(getWorldPlayDoiUrl(), '_self');
        return next();
    }

    sessionStorage.removeItem(WP_PLAY_HE_ATTEMPTED_STORAGE_KEY);

    const {productId} = store.state.brandConfig;
    const getUser = await WorldPlayService.getUser(msisdn, productId);
    const userData = {
        ...getUser,
        msisdn: getUser?.Msisdn || msisdn,
    };
    const cleanedUser = cleanMtnUser(userData);
    const userStatus = getUser?.Status?.toLowerCase();
    const validUser = userStatus === 'pending' || userStatus === 'active';

    if (validUser) {
        localStorage.setItem('msisdn', cleanedUser.msisdn || msisdn);
        await setUserDataMtn(cleanedUser, next, to);
        return;
    }

    window.open(getWorldPlayDoiUrl(), '_self');
    return next();
}

function sanitizeMsisdn(msisdn) {
    if (!msisdn) return null;
    const digitsOnly = `${msisdn}`.replace(/\D/g, '');
    if (digitsOnly.startsWith('27') && digitsOnly.length === 11) return digitsOnly;
    if (digitsOnly.startsWith('0') && digitsOnly.length === 10) return `27${digitsOnly.slice(1)}`;
    return null;
}

async function resolveWorldPlayMsisdn(urlParams) {
    const wpMsisdn = sanitizeMsisdn(urlParams.get('tn'));
    if (wpMsisdn) return wpMsisdn;

    const urlMsisdn = sanitizeMsisdn(urlParams.get('msisdn'));
    if (urlMsisdn) return urlMsisdn;

    const encryptedMsisdn = urlParams.get('e-msisdn');
    if (encryptedMsisdn) {
        try {
            const decryptResponse = await DcbService.decryptMsisdn(encryptedMsisdn);
            const decryptedMsisdn = sanitizeMsisdn(decryptResponse?.data?.rawMsisdn);
            if (decryptedMsisdn) return decryptedMsisdn;
        } catch (e) {
            console.error('Failed to decrypt e-msisdn for WP header enrichment', e);
        }
    }

    return sanitizeMsisdn(localStorage.getItem('msisdn'));
}

async function userAuthenticateEncryptedFlow(to, from, next) {
    if (store.state.user || to.name === 'Wifi' || to.name === 'DoiTwo' || to.name === 'TimeoutError') {
        store.commit('setIsLoading', false);
        return next();
    }
    store.commit('setIsLoading', true);
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token') ?? localStorage.getItem('token');
    const encryptedMsisdn = urlParams.get('encryptedMsisdn') ?? localStorage.getItem('encryptedMsisdn');
    const campaignId = urlParams.get('campaignId') || urlParams.get('cid');
    const googleClickId = urlParams.get('gclid');
    const clickId = urlParams.get('clickId');

    if (campaignId) setCampaignId(campaignId)
    if (googleClickId) setGoogleClickId(googleClickId)
    if (clickId) setClickId(clickId)
    if (encryptedMsisdn && token) {
        return await storeUserDetails(encryptedMsisdn, token, next);
    } else {
        store.commit('setIsLoading', false);
        return next({name: 'Wifi'});
    }
}

async function userAuthenticateNormalFlow(to, from, next) {
    const urlParams = new URLSearchParams(window.location.search);
    const encryptedMsisdn = urlParams.get('e-msisdn');

    if (store.state.user) {
        store.commit('setIsLoading', false);
        return next();
    }
    const userSessionData = getUserSessionData();
    if (userSessionData.msisdn) {
        await setUserData(userSessionData.msisdn, userSessionData.serviceId, next, to);
    }

    const allowedRoutes = ['TermsAndConditions', 'HomeView', 'NewsHome', 'MenuView', 'UpcomingFixtures', 'RequestOtp', 'NewsVideos', 'NewsArticles', 'NewsVideo', 'NewsArticle', 'NewsExclusive', 'BeastFixtures', 'BeastResults', 'BeastPlayers', 'BeastMatchDetail'];
    if (allowedRoutes.includes(to.name) && !encryptedMsisdn) return next();
    if (!allowedRoutes.includes(to.name) && !store.state.user) return next({name: 'RequestOtp'});

    const campaignId = urlParams.get('campaignId') || urlParams.get('cid');
    const googleClickId = urlParams.get('gclid');
    const msisdn = urlParams.get('msisdn') ?? localStorage.getItem('msisdn');
    const isMiniApp = urlParams.get('miniApp');
    const clickId = urlParams.get('clickId');

    if (msisdn && isMiniApp) return await miniAppLogin(msisdn, next);
    if (campaignId) setCampaignId(campaignId)
    if (googleClickId) setGoogleClickId(googleClickId)
    if (clickId) setClickId(clickId)
    if (encryptedMsisdn) return await encryptedMsisdnAuth(encryptedMsisdn, next, campaignId);

    store.commit('setIsLoading', false);
    return next();
}

async function miniAppLogin(msisdn, next) {
    localStorage.setItem('isMiniApp', true);
    await getMultiTierService(msisdn);
    return await redirectBasedOnUserStatus(next);
}

function setCampaignId(campaignId) {
    store.commit('setCampaignId', campaignId);
}

function setGoogleClickId(googleClickId) {
    store.commit('setGoogleClickId', googleClickId);
}

function setClickId(clickId) {
    store.commit('setClickId', clickId);
}

async function storeUserDetails(encryptedMsisdn, token, next) {
    const userDataResponse = await getUser(encryptedMsisdn, token);
    await setEncryptedUserData(userDataResponse, token, encryptedMsisdn);
    return await redirectBasedOnUserStatusMarketingFlow(userDataResponse.data, next);
}

async function redirectBasedOnUserStatusMarketingFlow(user, next) {
    //TODO look at this logic !!
    store.commit('setIsLoading', false);
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');

    if (user.status === "Active" && user.serviceId !== ServiceIds.free) return next({name: 'LandingPage'});
    else if (user.status === 'Content Blocked') return next({path: `/error/:${user.status}`})
    // else if (action) return await handleActionClick(user, action, next);
    else return next({name: 'DoiTwo'});
    // Need to implement this page else if(user?.contentBlockedEnabled) return next({name: 'ContentBlockOptIn'});
}

async function getUser(encryptedMsisdn, token) {
    const serviceId = await getMultiTierServiceV2(token);
    return await DcbService.getUserViaEncryptedMsisdn(encryptedMsisdn, token, serviceId);
}

async function setEncryptedUserData(userDataResponse, token = undefined, encryptedMsisdn = undefined) {
    await DcbService.trackLoginV2(token);
    store.commit('setUser', userDataResponse.data);
    store.commit('setToken', token);
    store.commit('setEncryptedMsisdn', encryptedMsisdn);
    localStorage.setItem('token', token);
    localStorage.setItem('encryptedMsisdn', encryptedMsisdn);
}

async function subscriptionIdAuth(subscriptionId, next, campaignId, entryToken) {
    store.commit('setIsLoading', true);
    const {msisdn} = (await DcbService.getUserViaSubscriptionId(subscriptionId, ServiceIds.dailyInternal, entryToken)).data ?? {};

    history.replaceState(history.state, '', location.pathname);

    if (!msisdn) {
        store.commit('setIsLoading', false);
        return next({name: 'HomeView'});
    }

    sessionStorage.setItem('msisdn', msisdn);

    if (campaignId) setCampaignId(campaignId)

    await getMultiTierService(msisdn);
    return await redirectBasedOnUserStatus(next, campaignId);
}

async function encryptedMsisdnAuth(encryptedMsisdn, next, campaignId) {
    const {rawMsisdn} = (await DcbService.decryptMsisdn(encryptedMsisdn)).data;
    sessionStorage.setItem(`msisdn`, rawMsisdn)
    await getMultiTierService(rawMsisdn);
    return await redirectBasedOnUserStatus(next, campaignId);
}

async function getMultiTierService(msisdn) {
    let {serviceId} = (await DcbService.getUserViaProductId(msisdn, store.state.brandConfig.productId)).data;
    store.commit('setServiceId', serviceId);
    store.commit('setMsisdn', msisdn);
}

async function getMultiTierServiceV2(token) {
    const getMultiTierServiceResponse = await DcbService.getMultiTierServiceV2(token);
    const getServiceIdByDescriptionResponse = getServiceIdByDescription(getMultiTierServiceResponse.data);
    setServiceIdValues(getServiceIdByDescriptionResponse);
    return getServiceIdByDescriptionResponse
}

function setServiceIdValues(serviceId) {
    localStorage.setItem('serviceId', serviceId);
    store.commit('setServiceId', serviceId);
}

async function redirectBasedOnUserStatus(next, campaignId) {
    const user = (await DcbService.getUserViaProductId(store.state.msisdn, store.state.brandConfig.productId)).data;
    store.commit('setUser', user);
    store.commit('setIsLoading', false);
    if (!user) return next({name: 'WelcomeView'});
    if (campaignId) {
        if (`${campaignId}` === `124` && !hasGameAccess(store.state.user)) {
            next({name: 'ConfirmSubscription', params: {price: `5`, interval: 'day'}, replace: true});
        } else return next({name: 'HomeView'});
    } else return next({name: 'HomeView'});
}

function getServiceIdByDescription(array) {
    return array[0].Description;
}

function redirectFreeUserFromPredictorAndFantasy(to, next) {
    if (!store.state.user && (to.path.includes('predictor') || to.path.includes('fantasy'))) return next({name: 'RequestOtp'});
    if (!hasGameAccess(store.state.user) && (to.path.includes('predictor') || to.path.includes('fantasy'))) return next({name: 'MenuView'});
}

async function setUserData(msisdn, serviceId, next, to) {
    let user
    store.commit('setMsisdn', msisdn);
    store.commit('setServiceId', serviceId);
    if (store.state.brandConfig.brand.includes('vc')) user = (await DcbService.getUserViaProductId(msisdn, store.state.brandConfig.productId)).data;
    if (store.state.brandConfig.brand.includes('mtn')) user = (await MtnZAService.getUserViaMsisdn(msisdn, store.state.brandConfig.productId)).data.user;
    // TODO: if (store.state.brandConfig.brand.includes('telkom')) user = (await TelkomZAService.getUserViaMsisdn(msisdn, store.state.brandConfig.productId)).data.user;
    store.commit('setUser', user);
    store.commit('setIsLoading', false);
    redirectFreeUserFromPredictorAndFantasy(to, next)
    await setPredictorUserData(to, msisdn)
    return next();
}

async function setUserDataMtn(user, next, to) {
    store.commit('setMsisdn', user.msisdn);
    store.commit('setServiceId', user.serviceId);
    // TODO: if (store.state.brandConfig.brand.includes('telkom')) user = (await TelkomZAService.getUserViaMsisdn(msisdn, store.state.brandConfig.productId)).data.user;
    store.commit('setUser', user);
    store.commit('setIsLoading', false);
    await setPredictorUserData(to, user.msisdn)
    return next();
}

async function setPredictorUserData(to, msisdn) {
    const allowedPaths = ['/predictor/upcoming-rounds', '/predictor/my-rounds'];
    if (!allowedPaths.includes(to.path)) return;
    const response = await PredictorService.getUserStats(msisdn);
    store.commit('setPredictorUserStats', response);
}
